import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design03/Header';
import {
  Sparkles,
  Award,
  Heart,
  Shield,
  Star,
  ArrowLeft,
  CheckCircle,
  Thermometer,
  Droplets,
  Mountain,
  Crown,
  Zap,
  Globe,
  ArrowRight,
  Diamond,
} from 'lucide-react';

const CinnamonPage = () => {
  return (
    <>
      {/* Head tags: React 19 hoists these into <head> */}
      <title>Ceylon Cinnamon - The True Cinnamon | Claude&apos;s Special</title>
      <meta
        name="description"
        content="Discover authentic Ceylon Cinnamon from Sri Lanka. Learn about the world's finest cinnamon variety, its health benefits, and why it's considered the gold standard of spices."
      />
      <meta
        name="keywords"
        content="Ceylon Cinnamon, true cinnamon, Cinnamomum verum, Sri Lankan cinnamon, health benefits, premium spices"
      />
      <meta property="og:title" content="Ceylon Cinnamon - The True Cinnamon" />
      <meta
        property="og:description"
        content="Authentic Ceylon Cinnamon from Sri Lanka - the world's finest cinnamon with unmatched quality and health benefits."
      />
      <meta property="og:type" content="article" />
      <link rel="canonical" href="https://claudesspecial.com/ceylon-cinnamon" />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Ceylon Cinnamon',
          description:
            "Authentic Ceylon Cinnamon from Sri Lanka. Premium quality spice exported to Australia.",
          brand: { '@type': 'Brand', name: "Claude's Special" },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'AUD',
            availability: 'https://schema.org/InStock',
            url: 'https://claudesspecial.com/ceylon-cinnamon',
          },
        })}
      </script>

      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-800/80 to-slate-900/90"></div>
          <div className="absolute inset-0 opacity-30">
            <img
              src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
              alt="Ceylon Cinnamon bark being harvested"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 w-40 h-40 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-gradient-to-br from-rose-400/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-7xl mx-auto px-4 pt-24">
          <div className="mb-10">
            <Link
              to="/"
              aria-label="Back to home"
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-full px-8 py-4 mb-10 hover:bg-gradient-to-r hover:from-amber-500/30 hover:to-yellow-500/30 transition-colors duration-200 border border-amber-400/30"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="font-bold text-lg">Back to Home</span>
            </Link>
          </div>

          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-full px-8 py-4 mb-10 border border-amber-400/30">
            <Diamond className="h-6 w-6 text-amber-400" />
            <span className="font-bold text-lg">Cinnamomum Verum</span>
            <Sparkles className="h-5 w-5 text-amber-400" />
          </div>

          <h1 className="text-7xl md:text-9xl font-black mb-8 leading-tight">
            <span className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 bg-clip-text text-transparent">
              Ceylon
            </span>
            <br />
            <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
              Cinnamon
            </span>
          </h1>

          <p className="text-3xl md:text-4xl mb-8 font-light text-amber-200">
            The World&apos;s Only True Luxury Cinnamon
          </p>

          <p className="text-xl mb-16 opacity-90 max-w-4xl mx-auto leading-relaxed">
            Scientifically known as Cinnamomum verum, Ceylon Cinnamon is nature&apos;s most precious spice,
            cultivated exclusively in Sri Lanka&apos;s mystical highlands for over 2,000 years.
          </p>
        </div>
      </section>

      {/* What Makes It Special */}
      <section className="py-32 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-rose-500 to-pink-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-24">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 rounded-full px-8 py-4 mb-8 border border-amber-400/30">
              <Star className="h-6 w-6" />
              <span className="font-bold text-lg">What Makes It Luxury</span>
            </div>
            <h2 className="text-6xl font-black text-white mb-8">
              The Crown Jewel of
              <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent"> Spices</span>
            </h2>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto">
              Unlike common cassia cinnamon, Ceylon Cinnamon offers unparalleled luxury quality, safety, and health benefits
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            <div className="group text-center">
              <div className="relative mb-10">
                <div className="w-28 h-28 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                  <Diamond className="h-14 w-14 text-slate-900" />
                </div>
                <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-6">True Luxury Cinnamon</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                The only authentic luxury cinnamon species (Cinnamomum verum) recognized by botanists worldwide,
                native exclusively to Sri Lanka.
              </p>
            </div>

            <div className="group text-center">
              <div className="relative mb-10">
                <div className="w-28 h-28 bg-gradient-to-br from-rose-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                  <Heart className="h-14 w-14 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-6">Ultra-Safe Luxury</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                Contains less than 0.004% coumarin compared to cassia&apos;s 5%, making it completely safe
                for daily luxury consumption and therapeutic use.
              </p>
            </div>

            <div className="group text-center">
              <div className="relative mb-10">
                <div className="w-28 h-28 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                  <Sparkles className="h-14 w-14 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center">
                  <Star className="h-5 w-5 text-slate-900" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-6">Exquisite Luxury Flavor</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                Delicate, sweet, and complex luxury flavor profile with subtle citrus notes that enhance
                both sweet and savory culinary creations.
              </p>
            </div>

            <div className="group text-center">
              <div className="relative mb-10">
                <div className="w-28 h-28 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                  <Award className="h-14 w-14 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center">
                  <Crown className="h-5 w-5 text-slate-900" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-6">Global Luxury Recognition</h3>
              <p className="text-gray-300 leading-relaxed text-lg">
                Preferred by world-renowned luxury chefs, health experts, and discerning consumers
                who demand the finest quality spices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cultivation Process */}
      <section className="py-32 bg-gradient-to-br from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 rounded-full px-6 py-3 mb-8 border border-amber-400/30">
                <Mountain className="h-5 w-5" />
                <span className="font-bold">From Garden to Global Luxury</span>
              </div>

              <h2 className="text-6xl font-black text-white mb-8 leading-tight">
                Ancient Wisdom Meets
                <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent"> Modern Luxury</span>
              </h2>

              <p className="text-2xl text-gray-300 mb-12 leading-relaxed">
                Our luxury Ceylon Cinnamon journey begins in Sri Lanka&apos;s pristine central highlands,
                where generations of skilled artisans have perfected the art of luxury cinnamon cultivation.
              </p>

              <div className="space-y-10">
                <div className="flex items-start space-x-8">
                  <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-3xl flex items-center justify-center shadow-xl">
                    <Mountain className="h-10 w-10 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4">Highland Luxury Terroir</h3>
                    <p className="text-gray-300 leading-relaxed text-lg">
                      Grown at optimal luxury altitudes of 500-2000 meters where the unique combination of
                      tropical climate, rich soil, and monsoon rains creates the perfect environment
                      for premium luxury cinnamon trees.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-8">
                  <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-xl">
                    <Droplets className="h-10 w-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4">Master Artisan Processing</h3>
                    <p className="text-gray-300 leading-relaxed text-lg">
                      Hand-peeled by luxury master craftsmen using traditional techniques passed down through
                      centuries, ensuring each quill maintains its delicate structure and aromatic oils.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-8">
                  <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-3xl flex items-center justify-center shadow-xl">
                    <Shield className="h-10 w-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-4">Luxury Quality Assurance</h3>
                    <p className="text-gray-300 leading-relaxed text-lg">
                      Every batch undergoes rigorous luxury testing for purity, moisture content, and essential
                      oil concentration to ensure only the finest cinnamon reaches international luxury markets.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-3xl transform rotate-12"></div>
              <div className="relative bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl border border-amber-500/20">
                <img
                  src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg"
                  alt="Traditional cinnamon processing in Sri Lanka"
                  className="w-full h-96 object-cover rounded-2xl mb-8"
                />
                <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-3xl p-8 shadow-xl text-slate-900">
                  <div className="text-center">
                    <div className="text-3xl font-black mb-2">2000+</div>
                    <div className="text-sm opacity-80">Years Luxury Heritage</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Health Benefits */}
      <section className="py-32 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-rose-500/20 to-pink-500/20 text-rose-400 rounded-full px-8 py-4 mb-8 border border-rose-400/30">
              <Heart className="h-6 w-6" />
              <span className="font-bold text-lg">Luxury Health &amp; Wellness</span>
            </div>
            <h2 className="text-6xl font-black text-white mb-8">
              Nature&apos;s Most Powerful
              <span className="bg-gradient-to-r from-rose-400 to-pink-400 bg-clip-text text-transparent"> Luxury Wellness Spice</span>
            </h2>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto">
              Ceylon Cinnamon isn&apos;t just a culinary luxury delight—it&apos;s a scientifically proven wellness enhancer
              with remarkable health benefits
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            <div className="group bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-rose-500/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Heart className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Luxury Cardiovascular Support</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Supports healthy cholesterol levels and promotes optimal blood circulation,
                  contributing to overall heart health and cardiovascular wellness.
                </p>
                <div className="flex items-center text-rose-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>Clinically Studied</span>
                </div>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-blue-500/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Thermometer className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Luxury Blood Sugar Balance</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  May help support healthy blood sugar levels and improve insulin sensitivity,
                  making it beneficial for metabolic health management.
                </p>
                <div className="flex items-center text-blue-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>Research Backed</span>
                </div>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-emerald-500/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Shield className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Luxury Antioxidant Powerhouse</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Packed with powerful antioxidants that help protect cells from oxidative stress
                  and support overall health and longevity.
                </p>
                <div className="flex items-center text-emerald-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>High ORAC Value</span>
                </div>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-amber-500/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Zap className="h-10 w-10 text-slate-900" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Luxury Anti-Inflammatory</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Natural anti-inflammatory compounds help reduce inflammation markers
                  and support joint health and overall comfort.
                </p>
                <div className="flex items-center text-amber-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>Natural Compounds</span>
                </div>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-purple-500/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <Star className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Luxury Cognitive Support</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  May support brain health and cognitive function through neuroprotective
                  compounds and enhanced blood flow to the brain.
                </p>
                <div className="flex items-center text-purple-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>Neuroprotective</span>
                </div>
              </div>
            </div>

            <div className="group bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-10 shadow-2xl hover:shadow-3xl transition-all duration-500 border border-green-500/20 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">Safe Luxury Daily Use</h3>
                <p className="text-gray-300 leading-relaxed mb-8 text-lg">
                  Unlike cassia cinnamon, Ceylon Cinnamon&apos;s ultra-low coumarin content
                  makes it completely safe for daily luxury consumption and therapeutic doses.
                </p>
                <div className="flex items-center text-green-400 font-bold">
                  <CheckCircle className="h-5 w-5 mr-3" />
                  <span>FDA Recognized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-rose-500/20 to-pink-500/20 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-full px-8 py-4 mb-10 border border-amber-400/30">
            <Diamond className="h-6 w-6 text-amber-400" />
            <span className="text-white font-bold text-lg">Ready to Experience Luxury?</span>
          </div>

          <h2 className="text-6xl md:text-7xl font-black text-white mb-8 leading-tight">
            Experience the
            <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent"> True Luxury Cinnamon Difference</span>
          </h2>

          <p className="text-2xl text-gray-200 mb-16 leading-relaxed max-w-4xl mx-auto">
            Join Australia&apos;s leading luxury importers and health-conscious consumers who choose authentic Ceylon Cinnamon
            for its unmatched quality, safety, and health benefits.
          </p>

          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <Link
              to="/#contact"
              className="group bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-600 hover:via-yellow-500 hover:to-amber-600 text-slate-900 px-12 py-6 rounded-full font-black text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center space-x-3"
            >
              <span>Get Luxury Wholesale Pricing</span>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
            <a
              href="mailto:exports@claudesspecial.lk"
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-amber-400/50 hover:border-amber-400/80 px-12 py-6 rounded-full font-black text-xl transition-all duration-300 backdrop-blur-xl"
            >
              Email Direct
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-20 border-t border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center">
                <Sparkles className="h-9 w-9 text-slate-900" />
              </div>
              <div>
                <div className="text-3xl font-black text-white">Claude&apos;s Special</div>
                <div className="text-amber-400 font-bold">Authentic Luxury Ceylon Cinnamon</div>
              </div>
            </div>
            <p className="text-gray-300 mb-8 max-w-3xl mx-auto text-lg">
              The world&apos;s finest luxury Ceylon Cinnamon, sourced directly from Sri Lanka&apos;s pristine highlands
              and delivered to Australia with uncompromising quality and authenticity.
            </p>
            <div className="flex justify-center space-x-10 text-gray-300 mb-10">
              <span className="flex items-center space-x-3">
                <Diamond className="h-5 w-5 text-amber-400" />
                <span className="text-lg">Premium Luxury Quality</span>
              </span>
              <span className="flex items-center space-x-3">
                <Shield className="h-5 w-5 text-emerald-400" />
                <span className="text-lg">Safe Daily Luxury Use</span>
              </span>
              <span className="flex items-center space-x-3">
                <Globe className="h-5 w-5 text-blue-400" />
                <span className="text-lg">Global Luxury Export</span>
              </span>
            </div>
            <p className="text-gray-500 text-lg">
              &copy; 2025 Claude&apos;s Special. All rights reserved. | Authentic Luxury Ceylon Cinnamon Exports
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default CinnamonPage;
